import React, {useState} from 'react';
import PropTypes from 'prop-types';
import styles from './signup.css';

export default function Signup ({onSignup, onSwitchToLogin}) {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        age: '',
        city: '',
        country: '',
        school: '',
        accountType: 'personal',
        licenseKey: '',
        hwid: '',
        password: '',
        confirmPassword: ''
    });

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleChange = e => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Required fields validation
        if (
            !formData.name ||
            !formData.email ||
            !formData.age ||
            !formData.city ||
            !formData.country ||
            !formData.password ||
            !formData.confirmPassword ||
            (formData.accountType === 'pro' && !formData.school) ||
            (formData.accountType === 'pro' && !formData.licenseKey)
        ) {
            setError('Please fill in all required fields');
            setLoading(false);
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address');
            setLoading(false);
            return;
        }

        // Age validation
        const age = parseInt(formData.age, 10);
        if (isNaN(age) || age < 1 || age > 120) {
            setError('Please enter a valid age');
            setLoading(false);
            return;
        }

        // Password length validation
        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            setLoading(false);
            return;
        }

        // Confirm password validation
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        let hwid = '';
        let mac = '';
        if (formData.accountType === 'pro') {
            try {
                const hwidResponse = await fetch('http://127.0.0.1:14001/get-machine-id', {
                    method: 'GET',
                    signal: AbortSignal.timeout(2000)
                });
                const hwidData = await hwidResponse.json();
                if (hwidData.success && hwidData.hwid) {
                    hwid = hwidData.hwid;
                    mac = hwidData.mac;
                } else {
                    setError('Failed to get Hardware ID. Make sure the License Agent is running.');
                    setLoading(false);
                    return;
                }
            } catch (err) {
                setError('License Agent is not reachable. Please start the License App.');
                setLoading(false);
                return;
            }
        }

        try {
            const response = await fetch('https://app.tifstudio.in/api/signup', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    age: formData.age,
                    city: formData.city,
                    country: formData.country,
                    school: formData.school,
                    password: formData.password,
                    tier: formData.accountType,
                    licenseKey: formData.licenseKey,
                    hwid: hwid,
                    mac: mac
                })
            });

            const data = await response.json();

            if (data.success) {
                if (data.sessionToken) {
                    localStorage.setItem('session_token', data.sessionToken);
                }
                // Signup successful, call onSignup callback
                onSignup(formData);
            } else {
                setError(data.message || 'Signup failed. Please try again.');
                setLoading(false);
            }
        } catch (error) {
            console.error('Signup error:', error);
            setError('Unable to connect to server. Please try again.');
            setLoading(false);
        }
    };

    return (
        <div className={styles.signupPage}>
            <div className={styles.signupBox}>
                <h2 className={styles.brand}>Create your account</h2>

                <form onSubmit={handleSubmit}>
                    {/* Full Name */}
                    <div className={styles.field}>
                        <input
                            type="text"
                            name="name"
                            placeholder="Full Name *"
                            value={formData.name}
                            onChange={handleChange}
                            disabled={loading}
                            required
                        />
                    </div>

                    {/* Email */}
                    <div className={styles.field}>
                        <input
                            type="email"
                            name="email"
                            placeholder="Email *"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={loading}
                            required
                        />
                    </div>

                    {/* Age & City (Flex) */}
                    <div className={styles.formRow}>
                        <div className={styles.field}>
                            <input
                                type="number"
                                name="age"
                                placeholder="Age *"
                                value={formData.age}
                                onChange={handleChange}
                                disabled={loading}
                                min="1"
                                max="120"
                                required
                            />
                        </div>

                        <div className={styles.field}>
                            <input
                                type="text"
                                name="city"
                                placeholder="City *"
                                value={formData.city}
                                onChange={handleChange}
                                disabled={loading}
                                required
                            />
                        </div>
                    </div>

                    {/* Country & School (Flex) */}
                    <div className={styles.formRow}>
                        <div className={styles.field}>
                            <input
                                type="text"
                                name="country"
                                placeholder="Country *"
                                value={formData.country}
                                onChange={handleChange}
                                disabled={loading}
                                required
                            />
                        </div>

                        <div className={styles.field}>
                            <input
                                type="text"
                                name="school"
                                placeholder={formData.accountType === 'pro' ? 'School / Institution *' : 'School / Institution (Optional)'}
                                required={formData.accountType === 'pro'}
                                value={formData.school}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        </div>
                    </div>


                    {/* Account Type */}
                    <div
                        className={styles.field}
                        style={{textAlign: 'left'}}
                    >
                        <label style={{display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px', marginLeft: '2px'}}>Account Type</label>
                        <select
                            name="accountType"
                            value={formData.accountType}
                            onChange={e => {
                                handleChange(e);
                                if (e.target.value !== 'pro') {
                                    setFormData(prev => ({...prev, licenseKey: ''}));
                                }
                            }}
                            disabled={loading}
                            style={{width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #c8daff', outline: 'none', fontSize: '14px', backgroundColor: '#fff', boxSizing: 'border-box'}}
                        >
                            <option value="personal">Personal Account</option>
                            <option value="pro">Institutional License</option>
                        </select>
                    </div>

                    {/* License Key */}
                    {formData.accountType === 'pro' && (
                        <div className={styles.field}>
                            <input
                                type="text"
                                name="licenseKey"
                                placeholder="License Key *"
                                value={formData.licenseKey}
                                onChange={handleChange}
                                disabled={loading}
                                required
                            />
                        </div>
                    )}

                    {/* Password */}
                    <div className={styles.field}>
                        <div className={styles.passwordWrapper}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                placeholder="Password *"
                                value={formData.password}
                                onChange={handleChange}
                                disabled={loading}
                                required
                                minLength="6"
                            />
                            <button
                                type="button"
                                className={styles.togglePassword}
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={loading}
                            >
                                {showPassword ? 'Hide' : 'Show'}
                            </button>
                        </div>
                    </div>

                    {/* Confirm Password */}
                    <div className={styles.field}>
                        <div className={styles.passwordWrapper}>
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                name="confirmPassword"
                                placeholder="Confirm Password *"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                disabled={loading}
                                required
                                minLength="6"
                            />
                            <button
                                type="button"
                                className={styles.togglePassword}
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                disabled={loading}
                            >
                                {showConfirmPassword ? 'Hide' : 'Show'}
                            </button>
                        </div>
                    </div>

                    {/* Error */}
                    {error && <div className={styles.error}>{error}</div>}

                    {/* Submit */}
                    <button
                        className={styles.btn}
                        disabled={loading}
                        type="submit"
                    >
                        {loading ? <span className={styles.spinner} /> : 'Sign Up'}
                    </button>
                </form>


                {/* Switch to Login */}
                <div className={styles.switchLink}>
                    Already have an account?{' '}
                    <button
                        className={styles.linkBtn}
                        onClick={onSwitchToLogin}
                        type="button"
                    >
                        Sign In
                    </button>
                </div>
            </div>
        </div>
    );
}

Signup.propTypes = {
    onSignup: PropTypes.func.isRequired,
    onSwitchToLogin: PropTypes.func.isRequired
};
